/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Bilingual Translation Studio & CAT Workspace View (Task 10.5.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useEffect } from 'react';
import {
  Download,
  ArrowLeft,
} from 'lucide-react';
import { SophiaIcon } from '@sofia/assets';
import { useTranslation } from '../i18n';
import { DualColumnEditor } from '../components/cat/DualColumnEditor';
import { CatProjectsList } from '../components/cat/CatProjectsList';
import { ExportModal } from '../components/cat/ExportModal';
import { NewProjectModal } from '../components/cat/NewProjectModal';
import { useCatEditorStore } from '../stores/useCatEditorStore';
import { useCatProjectsStore } from '../stores/useCatProjectsStore';

export const CatTranslationView: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'projects' | 'workbench'>('workbench');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);

  const { calculateProgress, segments } = useCatEditorStore();
  const { projects, activeProjectId, loadProjectToEditor, syncActiveProjectFromEditor } =
    useCatProjectsStore();

  const progress = calculateProgress();
  const activeProject = projects.find((p) => p.id === activeProjectId) || projects[0];

  // Sync segment progress to active project in projects store
  useEffect(() => {
    if (segments.length > 0) {
      syncActiveProjectFromEditor(segments);
    }
  }, [segments, syncActiveProjectFromEditor]);

  const handleOpenProjectInWorkbench = (projectId: string) => {
    loadProjectToEditor(projectId);
    setActiveTab('workbench');
  };

  const handleOpenExportForProject = (projectId: string) => {
    loadProjectToEditor(projectId);
    setShowExportModal(true);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] p-4 md:p-6 space-y-4">
      {/* Top Studio Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Left: Branding & Subtitle */}
        <div className="flex items-center gap-3">
          {activeTab === 'workbench' && (
            <button
              type="button"
              onClick={() => setActiveTab('projects')}
              title={t('cat.backToProjects')}
              className="p-2 rounded-lg border border-surface-border bg-surface text-foreground-muted hover:text-foreground hover:bg-surface-elevated transition-colors"
            >
              <ArrowLeft className="size-4" />
            </button>
          )}

          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-primary/10 text-primary border border-primary/20">
              <SophiaIcon name="nano-tech" size={20} className="shrink-0" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold font-heading text-foreground">
                  {t('cat.title')}
                </h1>
                {activeTab === 'workbench' && activeProject && (
                  <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-surface-elevated border border-surface-border text-foreground">
                    <span className="truncate max-w-[180px]">{activeProject.title}</span>
                    <span className="font-mono text-primary font-bold">
                      {progress.percent}%
                    </span>
                  </span>
                )}
              </div>
              <p className="text-xs text-foreground-muted">
                {t('cat.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Right: Tab Navigation & Primary Action Buttons */}
        <div className="flex items-center gap-2">
          {/* View Mode Tabs */}
          <div className="flex items-center p-1 rounded-xl border border-surface-border bg-surface shadow-xs">
            <button
              type="button"
              onClick={() => setActiveTab('projects')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeTab === 'projects'
                  ? 'bg-primary text-primary-fg shadow-xs'
                  : 'text-foreground-muted hover:text-foreground hover:bg-surface-elevated/40'
              }`}
            >
              <SophiaIcon name="solution" size={14} className="shrink-0" />
              <span>{t('cat.tabProjects')}</span>
              <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-surface-elevated/80 text-foreground font-mono font-bold">
                {projects.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('workbench')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeTab === 'workbench'
                  ? 'bg-primary text-primary-fg shadow-xs'
                  : 'text-foreground-muted hover:text-foreground hover:bg-surface-elevated/40'
              }`}
            >
              <SophiaIcon name="nano-tech" size={14} className="shrink-0" />
              <span>{t('cat.tabWorkbench')}</span>
            </button>
          </div>

          {/* Quick Action Buttons */}
          {activeTab === 'workbench' ? (
            <button
              type="button"
              onClick={() => setShowExportModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-primary text-primary-fg hover:bg-primary/90 shadow-sm transition-colors whitespace-nowrap"
            >
              <Download className="size-3.5" />
              <span>{t('cat.exportDocument')}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowNewProjectModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-primary text-primary-fg hover:bg-primary/90 shadow-sm transition-colors whitespace-nowrap"
            >
              <SophiaIcon name="algorithm" size={14} className="shrink-0" />
              <span>{t('cat.newProjectBtn')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Main Body View */}
      {activeTab === 'workbench' ? (
        <div className="flex-1 min-h-0">
          <DualColumnEditor />
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
          <CatProjectsList
            onOpenProject={handleOpenProjectInWorkbench}
            onExportProject={handleOpenExportForProject}
          />
        </div>
      )}

      {/* Multi-Format Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      />

      {/* New Project Drag & Drop Modal */}
      <NewProjectModal
        isOpen={showNewProjectModal}
        onClose={() => setShowNewProjectModal(false)}
        onProjectCreated={(id) => {
          setShowNewProjectModal(false);
          handleOpenProjectInWorkbench(id);
        }}
      />
    </div>
  );
};
